/**
 * What ONE line on a document is worth — the single answer, for everyone.
 *
 * Estimate, invoice, job, preview, costing, tax and reporting all have to agree
 * about what a part contributes, and they only agree if they ask the same
 * function. Three separate implementations of "what is this part worth" is how
 * the current bugs arose — one of them was blind to cost entirely.
 *
 * Seven fields, and every one of them is INDEPENDENT:
 *
 *   cost               what we pay per unit (persisted as `costBasis`)
 *   basePrice          the pricebook's price for the item
 *   unitPrice          the price actually used on THIS document — the operator
 *                      may type over the pricebook's (persisted as
 *                      `manualPriceOverride`; see `unitPriceOf`)
 *   quantity           how many
 *   chargeable         whether the line contributes revenue
 *   visibleToCustomer  whether the line renders on the customer's document
 *   taxable            whether it is taxed
 *
 * The two contribution rules, and there are only two:
 *
 *   revenue contribution = chargeable ? unitPrice x quantity : 0
 *   cost contribution    = cost x quantity        ALWAYS — regardless of
 *                                                 chargeable or visibility
 *
 * Three consequences worth stating, because each one has been got wrong:
 *
 *   `price = 0` with `chargeable = true` charges $0 and IS billable. It is not
 *   the same thing as a non-chargeable line, and the two must not be inferred
 *   from one another.
 *
 *   `price > 0` with `chargeable = false` KEEPS its real stored price and
 *   contributes $0 revenue. The price is still true; it simply is not charged,
 *   so nothing here ever zeroes it in storage.
 *
 *   Visibility changes NOTHING about money. Not totals, not tax, not cost, not
 *   revenue, not margin. It decides one thing: whether the row prints.
 */

/**
 * How a document charges for a part.
 *
 * The decision belongs to the SERVICE-PART relationship, not the catalog item:
 * the same water heater is included in one service's price and sold separately
 * beside another. The catalog's `chargeable` only supplies the default — see
 * `pricingTreatmentOf`.
 */
export const PRICING_TREATMENTS = ['included_in_service', 'separate_charge'] as const;

export type PricingTreatment = (typeof PRICING_TREATMENTS)[number];

/**
 * A line, in as many spellings as the persisted shapes actually use.
 *
 * Nullable throughout: these arrive from JSON snapshots where an absent value
 * is `null`, not `undefined`, and requiring one or the other would push casts
 * onto every caller — which is exactly where a real type error then hides.
 */
export interface LineEconomics {
  type?: string | null;
  quantity?: number | null;

  /** What we pay per unit. `costBasis` is the persisted spelling. */
  costBasis?: number | null;
  cost?: number | null;

  /** The pricebook's price for the item, snapshotted onto the line. */
  basePrice?: number | null;
  /** The operator's price for THIS document — the persisted override. */
  manualPriceOverride?: number | null;
  /** Job/financials shapes name the already-resolved price this way. */
  unitPrice?: number | null;
  price?: number | null;
  /** Resolved document amounts, present on read/public line snapshots. */
  lineSubtotal?: number | null;
  lineTotal?: number | null;

  /**
   * MATERIAL / EQUIPMENT only: whether the CATALOG bills this part on its own
   * line. Only the default for `pricingTreatment`.
   */
  chargeable?: boolean | null;
  /** How THIS document charges for the part. Absent derives from `chargeable`. */
  pricingTreatment?: PricingTreatment | null;

  taxable?: boolean | null;

  /** The canonical, positive form of visibility. */
  visibleToCustomer?: boolean | null;
  /** Persisted spelling on estimate / invoice / job line snapshots. */
  hiddenFromCustomer?: boolean | null;
  /** Persisted spelling on a pricebook service component. */
  showInDetail?: boolean | null;

  isSelected?: boolean | null;
  isOptional?: boolean | null;
}

const num = (value: number | null | undefined): number =>
  Number.isFinite(value) ? (value as number) : 0;

const finite = (value: number | null | undefined): number | null =>
  value != null && Number.isFinite(value) ? (value as number) : null;

const round2 = (value: number): number => Math.round(value * 100) / 100;

const normalizedType = (type: string | null | undefined): string =>
  String(type ?? '')
    .trim()
    .toUpperCase();

export function quantityOf(line: LineEconomics): number {
  return num(line.quantity);
}

/** What one unit costs us. Read whatever the shape calls it. */
export function unitCostOf(line: LineEconomics): number {
  return finite(line.costBasis) ?? num(line.cost);
}

/** The pricebook's price for this item, as snapshotted onto the line. */
export function basePriceOf(line: LineEconomics): number {
  return num(line.basePrice);
}

/**
 * The price actually used on THIS document.
 *
 * Deliberately DERIVED rather than stored as a third field. The override the
 * operator types is already persisted — `manualPriceOverride` on every document
 * shape and in the operations pricing engine — and `basePrice` is already the
 * pricebook's. `unitPrice` is the name for the answer, not a new place to keep
 * it: adding a column would give the same number two homes that can disagree,
 * and the engine would keep pricing off the one it already reads.
 *
 * `unitPrice` / `price` are accepted because job-financials shapes name the
 * resolved amount that way; they sit below the override for the same reason the
 * engine does — an explicit override is the operator's last word.
 *
 * A price that comes out of a STRATEGY (cost-plus, labor-only, time and
 * materials) is not readable from these fields; those callers resolve it and
 * pass it in as `options.unitPrice` below.
 */
export function unitPriceOf(line: LineEconomics): number {
  return (
    finite(line.manualPriceOverride) ??
    finite(line.unitPrice) ??
    finite(line.price) ??
    num(line.basePrice)
  );
}

/** Whether this document prices the line at something other than the pricebook. */
export function isManualPriceOverride(line: LineEconomics): boolean {
  const override = finite(line.manualPriceOverride);
  return override !== null && override !== num(line.basePrice);
}

/**
 * Can this type be left unbilled?
 *
 * Only parts. A SERVICE, BUNDLE or MEMBERSHIP is the thing being sold, and one
 * that bills nothing is not a sale — so the flag is neither offered for those
 * types nor honoured on one that arrives carrying it. Lockstep with
 * `canBeNonChargeable` in operations; if these disagree an API caller can zero
 * a service on one side of the wire and not the other.
 */
export function canBeNonChargeable(type: string | null | undefined): boolean {
  const normalized = normalizedType(type);
  return normalized === 'MATERIAL' || normalized === 'EQUIPMENT';
}

/**
 * May a document carry MORE THAN ONE line of the same catalog item?
 *
 * Only parts, and for the same reason they are the only types that can be left
 * unbilled: a part is a quantity of stuff, and the same stuff turns up twice for
 * honest reasons. NOT-638 is the report — a service that includes parts
 * materializes a real line per part, so a tech who used one more fitting than
 * "Water Heater Install" assumes has to be able to add that fitting again as the
 * catalog item it is. Excluding it left a custom line as the only way through,
 * which drops `lineItemId` and the cost tracking with it.
 *
 * MEMBERSHIP is the type this exists to keep out, and the rule is the server's,
 * not a preference: `assertSelectedMembershipPlansUnique` rejects an estimate
 * carrying two SELECTED lines for one plan — "A membership plan can only be
 * selected once per estimate." A membership line also cannot be deselected in
 * the editor (toggling optional forces a membership back to required/selected),
 * so a second one is not a row the operator can talk their way out of: the
 * estimate simply stops saving. Refusing the add is the only place that reads
 * as an answer rather than as a failure.
 *
 * SERVICE and BUNDLE are the conservative half of this answer. Nothing found
 * keys them by catalog id — the pricing engine matches lines positionally and
 * says so, precisely because one item can sit on several lines — so two of the
 * same install on one estimate looks legitimate. It is simply not what NOT-638
 * asked for, and widening it is a deliberate decision with its own evidence,
 * not a side effect of this one.
 */
export function canRepeatOnDocument(type: string | null | undefined): boolean {
  const normalized = normalizedType(type);
  return normalized === 'MATERIAL' || normalized === 'EQUIPMENT';
}

/**
 * The catalog ids a document's item searches must stop offering.
 *
 * ONE definition, because the estimate editor and the estimate-template editor
 * both build pickers and a rule split across them is a rule that drifts. A line
 * whose type may repeat contributes nothing here; everything else contributes
 * the catalog id it already occupies.
 *
 * Blank ids are dropped: a hand-typed line carries a client-minted id that
 * matches no catalog row, so excluding it could only ever hide the wrong thing.
 */
export function nonRepeatableLineItemIds(
  lines: readonly { lineItemId?: string | null; type?: string | null }[],
): string[] {
  return lines
    .filter((line) => Boolean(line.lineItemId) && !canRepeatOnDocument(line.type))
    .map((line) => line.lineItemId as string);
}

/**
 * Does a line of this type START billed when it is put on a document?
 *
 * MATERIAL and EQUIPMENT do not. They are costs used to perform the work unless
 * the operator explicitly decides to sell that line separately, so both start
 * unbilled and the operator ticks Chargeable for the sale where it genuinely is
 * a separate charge. Defaulting either one the other way adds its price to the
 * document before the operator has made that decision.
 *
 * Everything else cannot be unbilled at all (`canBeNonChargeable`) and the
 * answer here is moot for it.
 *
 * A DEFAULT FOR A NEW LINE ONLY, and deliberately not the same question as
 * `isChargeable`, which reads an ABSENT flag as billed. Nothing already saved
 * moves: this decides what the picker writes onto a line being created, and
 * every document that exists keeps the flag it was stored with.
 */
export function defaultChargeableForType(type: string | null | undefined): boolean {
  return !['MATERIAL', 'EQUIPMENT'].includes(normalizedType(type));
}

/**
 * Whether a line is billed at its own price.
 *
 * Undefined is TRUE, deliberately: a part put on a document is a part being
 * sold, and every line saved before the flag existed was billed. Reading a
 * missing value as "not billed" would stop charging for them.
 *
 * The flag belongs to the LINE, never to the catalog item behind it — the same
 * filter is included in one sale and charged in the next, so a catalog answer
 * would be wrong for one of them.
 */
export function isChargeable(line: LineEconomics): boolean {
  return line.chargeable !== false;
}

/**
 * The treatment to RECORD on a service component the operator has just added.
 *
 * A part put INSIDE a service is normally bought to be consumed by it, and the
 * service's price is what recovers it. Billing it again on its own row is the
 * exception, so that is the thing the operator switches to.
 *
 * The opposite of a part added straight onto a document, which is a part being
 * sold and starts billed. The two are different questions about different
 * sales, which is exactly why neither of them lives on the catalog item.
 *
 * DEFAULT FOR A NEW ROW ONLY. A component already saved with no treatment
 * keeps reading as `separate_charge` (`pricingTreatmentOf`), which is what
 * every service saved before this control existed prices at.
 */
export const NEW_COMPONENT_PRICING_TREATMENT: PricingTreatment = 'included_in_service';

/**
 * How this document charges for this part.
 *
 * The relationship answers first; the line's own `chargeable` is the default,
 * and an absent flag still means chargeable — so every component and every
 * saved line that predates any of this derives `separate_charge` and prices
 * exactly as it always has.
 */
export function pricingTreatmentOf(line: LineEconomics): PricingTreatment {
  if (line.pricingTreatment === 'included_in_service') return 'included_in_service';
  if (line.pricingTreatment === 'separate_charge') return 'separate_charge';
  return isChargeable(line) ? 'separate_charge' : 'included_in_service';
}

/**
 * THE chargeability question, for money: does this line contribute revenue?
 *
 * Everything that is not a part is always chargeable — see `canBeNonChargeable`.
 * For a part it is the relationship's treatment, which the catalog flag only
 * defaults.
 */
export function chargesRevenue(line: LineEconomics): boolean {
  if (!canBeNonChargeable(line.type)) return true;
  return pricingTreatmentOf(line) === 'separate_charge';
}

/**
 * THE selection question: is this line part of the sale?
 *
 * The stored flag, read the way the ENGINE reads it — `isSelected !== false`,
 * `PricingLineInput.isSelected` taken literally — because what the customer is
 * charged is what the server prices. `customerApprove` refuses an approval
 * that submits a non-optional line deselected, so the customer cannot produce
 * this shape; an import or an API caller can, and the server prices such a row
 * as OUT. Reading `!isOptional || isSelected !== false` here left the quote,
 * the PDF and the job DTO counting a line that approval and conversion drop.
 * The approval PAYLOAD still forces every non-optional line selected — that is
 * a contract about what may be submitted, isolated to `approvalLineItems`, not
 * a second answer to this question.
 *
 * Stated once because the two halves of the same document answered it
 * differently. The pricing preview asked `!isOptional || isSelected !== false`;
 * costing asked only `isSelected !== false`, so a line unticked WITHOUT being
 * offered earned revenue and cost nothing, and margin was overstated by exactly
 * that line. A selection rule implemented twice is a margin that lies the
 * moment the two drift, so revenue and cost both read this.
 */
export function isLineSold(line: LineEconomics): boolean {
  return line.isSelected !== false;
}

/**
 * Only zero-priced EQUIPMENT has a real customer-visibility decision.
 *
 * A non-zero part price must be explained by its own customer line. A material
 * with no price is internal consumable scope and never prints. Zero-priced
 * equipment is the one remaining choice: it may be named as installed without
 * inventing a charge. Writers ask this before inheriting a catalog visibility
 * flag; readers enforce the same invariant in `rendersOnCustomerDocument`.
 */
function hasNonZeroCustomerPrice(line: LineEconomics): boolean {
  if (!chargesRevenue(line)) return false;
  return (
    unitPriceOf(line) !== 0 ||
    num(line.lineSubtotal) !== 0 ||
    num(line.lineTotal) !== 0
  );
}

export function canHoldVisibilityDecision(line: LineEconomics): boolean {
  return (
    normalizedType(line.type) === 'EQUIPMENT' && !hasNonZeroCustomerPrice(line)
  );
}

export interface ContributionOptions {
  /**
   * The unit price a pricing STRATEGY resolved for this line (cost-plus,
   * labor-only, time and materials, a materialized dynamic service). Supplied
   * by the caller that knows the strategy; without it the stored unit price is
   * used, which is the right answer for every FLAT line.
   */
  unitPrice?: number;
  /**
   * The line's own already-computed billable amount (post line/membership/
   * document discount, for example). Tax and discount helpers take this so they
   * never re-derive a number the pricing pass has already settled.
   */
  billableAmount?: number;
}

/**
 * RULE ONE: revenue = chargeable ? unitPrice x quantity : 0.
 *
 * A non-chargeable line keeps its real stored price — nothing here writes back
 * — and simply contributes nothing. A chargeable line priced at $0 contributes
 * $0 and is still billable; those are different facts about different fields
 * and neither may be inferred from the other.
 */
export function revenueContribution(line: LineEconomics, options?: ContributionOptions): number {
  if (!chargesRevenue(line)) return 0;
  const unitPrice = finite(options?.unitPrice) ?? unitPriceOf(line);
  return round2(unitPrice * quantityOf(line));
}

/**
 * RULE TWO: cost = cost x quantity, ALWAYS.
 *
 * Blind to chargeability and to visibility, on purpose. A part we do not charge
 * for separately still costs what it costs, and a part the customer never sees
 * still costs what it costs — dropping either would report a margin the job
 * does not earn. Stated here rather than left to be inferred from the absence
 * of a check.
 *
 * A service billed by time has an HOUR for its unit, so its caller passes the
 * hourly cost and the hours; the rule is the same multiplication.
 */
export function costContribution(line: LineEconomics): number {
  return round2(unitCostOf(line) * quantityOf(line));
}

/** Revenue minus cost. Negative is a real (bad) number and is not clamped. */
export function marginContribution(line: LineEconomics, options?: ContributionOptions): number {
  return round2(revenueContribution(line, options) - costContribution(line));
}

/** Absent means taxable — every line saved before the flag existed was taxed. */
export function isTaxable(line: LineEconomics): boolean {
  return line.taxable !== false;
}

/**
 * What tax is charged on.
 *
 * Billable amounts ONLY. A non-chargeable part with a non-zero stored price
 * must never create taxable revenue: nobody is being asked to pay it, so taxing
 * it invents tax on money that is not on the bill.
 */
export function taxableBase(line: LineEconomics, options?: ContributionOptions): number {
  if (!isTaxable(line)) return 0;
  return billableAmount(line, options);
}

/**
 * What discounts come off.
 *
 * Billable amounts only, for the same reason as tax: discounting the stored
 * price of a part nobody is charged for would hand the customer money against a
 * charge that does not exist.
 *
 * Neither this nor `taxableBase` clamps at zero. A negative amount is a real
 * number — a credit line, a below-cost price — and the callers that genuinely
 * must not go negative already say so at their own boundary; clamping here
 * would quietly disagree with the subtotal they compute from the same figure.
 */
export function discountableBase(line: LineEconomics, options?: ContributionOptions): number {
  return billableAmount(line, options);
}

/**
 * What this line actually bills — the amount tax and discounts both work from.
 *
 * With no amount supplied it IS the revenue contribution. With one supplied
 * (already discounted, already bundled) it is that amount, still gated on
 * chargeability: a line that charges nothing has no billable amount, whatever
 * was computed upstream.
 *
 * Exported separately from `taxableBase` because taxABILITY is a different
 * question with a different owner — the tax engine has to emit a snapshot for a
 * non-taxable line rather than simply not being asked.
 */
export function billableAmount(line: LineEconomics, options?: ContributionOptions): number {
  const supplied = finite(options?.billableAmount);
  if (supplied === null) return revenueContribution(line, options);
  return chargesRevenue(line) ? supplied : 0;
}

/**
 * The visibility FLAG, when somebody has actually stated one.
 *
 * `undefined` means nobody has said — which is not the same as "hidden", and is
 * why this is separated from `rendersOnCustomerDocument` below.
 *
 * ONE representation: the positive `visibleToCustomer`. Three spellings are
 * READ because three already exist in storage, and this is the only place that
 * maps them:
 *
 *   visibleToCustomer   the canonical form. New writers use this, and it is
 *                       authoritative in BOTH directions.
 *   hiddenFromCustomer  estimate / invoice / job line snapshots.
 *   showInDetail        a pricebook service component's default for its part.
 *
 * The legacy pair is read as a VETO only: `hiddenFromCustomer: true` and
 * `showInDetail: false` hide, and their opposites state nothing. That asymmetry
 * is deliberate rather than sloppy. The estimate editor normalises every line
 * to `hiddenFromCustomer: item.hiddenFromCustomer === true`, so a plain `false`
 * is a default written by a mapper, not an operator's decision — treating it as
 * one would drag every non-chargeable material onto customer documents that
 * have never shown them. An operator who genuinely wants such a line shown sets
 * the canonical field.
 *
 * The persisted spellings are NOT migrated either: a JSON snapshot carrying
 * `hiddenFromCustomer: true` would come back VISIBLE the moment the reader
 * stopped understanding it, and a rename cannot reach documents already sent.
 * Storage keeps what it has; everything downstream sees one positive field.
 */
export function visibleToCustomerFlag(line: LineEconomics): boolean | undefined {
  if (typeof line.visibleToCustomer === 'boolean') return line.visibleToCustomer;
  if (line.hiddenFromCustomer === true) return false;
  if (line.showInDetail === false) return false;
  return undefined;
}

/** The flag, resolved. Missing means VISIBLE — silence has never hidden a line. */
export function visibleToCustomer(line: LineEconomics): boolean {
  return visibleToCustomerFlag(line) ?? true;
}

/**
 * Whether this line prints on the CUSTOMER's document.
 *
 * Priced material/equipment always prints because its amount must be explained.
 * Zero-priced material never prints. Zero-priced equipment follows its
 * visibility flag and defaults visible, so the customer may still see what is
 * installed.
 * Other line types keep the ordinary explicit-flag/default-visible behavior.
 */
export function rendersOnCustomerDocument(line: LineEconomics): boolean {
  // A priced line explains money in the total and therefore cannot be hidden.
  // With no price, consumable material is internal scope; zero-priced equipment
  // is the only part for which customer visibility remains a real choice.
  const type = normalizedType(line.type);
  if (
    (type === 'MATERIAL' || type === 'EQUIPMENT') &&
    hasNonZeroCustomerPrice(line)
  ) {
    return true;
  }
  if (type === 'MATERIAL') return false;
  return visibleToCustomerFlag(line) ?? true;
}

/**
 * Whether the customer's document puts a MONETARY AMOUNT against this row.
 *
 * A visible non-chargeable line reads as "Included": it is part of what they
 * are buying, and there is no separate charge to state. Rendering only — the
 * totals never consult this.
 *
 * PER-LINE. A customer document draws ROWS, and a rolled-up row stands for a
 * service plus the parts folded into it, so the renderers ask
 * `rowShowsMonetaryAmount` in `service-components.ts` instead. This is the rule
 * that one is built out of.
 */
export function showsMonetaryAmount(line: LineEconomics): boolean {
  return rendersOnCustomerDocument(line) && hasNonZeroCustomerPrice(line);
}

/**
 * What an INTERNAL view prints where no price applies.
 *
 * A dash and `$0.00` are different claims. `$0.00` asserts a price — and a
 * chargeable line priced at zero is a real, legitimate quote that has to keep
 * printing `$0.00`. A dash says the question does not arise: this line charges
 * nothing, so it has no price to state.
 */
export const NO_PRICE_DISPLAY = '—';

/**
 * What a CUSTOMER document prints where no price applies — NOT-637.
 *
 * The same claim as the dash above, in the register the reader is owed. A dash
 * is an operator's shorthand: it says a number is absent and leaves why to the
 * person who already knows. A customer reading it on the unit being installed
 * learns only that something is missing from their quote.
 *
 * The word is the fact. An included part is paid for inside the service that
 * installs it, and "Included" is exactly that sentence — which is why
 * `SPEC-line-item-model.md` writes the rule as ‘show as "Included", or with no
 * monetary amount’. The bug it answers is the other reading: a 40-gal water
 * heater printed at `$0.00` beside a $2,950 service, so the customer read the
 * heater as free and asked why the labour cost $2,950. Every monetary cell on a
 * non-chargeable row says this, because a row that states a price in one column
 * and nothing in another invites exactly that arithmetic again.
 *
 * ONE spelling for all five customer surfaces — the estimate PDF, the public
 * quote page, the client portal, the public invoice and the invoice PDF. They
 * render the same document; two vocabularies for one rule is how they drift.
 */
export const INCLUDED_PRICE_DISPLAY = 'Included';

/**
 * A line's PRICE as an INTERNAL surface renders it — the estimate editor, the
 * internal read-only line table.
 *
 * INTERNAL ONLY, on purpose, and still so after NOT-637. The customer's
 * document has stopped printing `$0.00` against a part it charges nothing for
 * — but what it prints instead is the WORD, `INCLUDED_PRICE_DISPLAY`, decided
 * per ROW by `rowShowsMonetaryAmount`. The dash stays an operator's shorthand
 * for "no price applies here", which a customer reading it on the thing being
 * installed cannot be expected to unpack. Nothing under `components/documents`
 * or `components/public` may call this.
 *
 * PER-LINE ONLY, equally on purpose. A section or document TOTAL is an
 * arithmetic result: the sum of the lines that charge, which is genuinely
 * `$0.00` when none of them do. Dashing a total would replace a computed number
 * with a claim about the lines that produced it.
 *
 * Displaying, never WRITING: a non-chargeable line keeps its real stored price
 * (see `unitPriceOf`), and nothing here zeroes it. This only decides what is
 * rendered where an amount is TEXT.
 *
 * Whether the editor still offers a price CONTROL is a separate, owner's
 * decision, and the answer is no: an unbilled part shows its cost and no price
 * or markup, so the operator is never invited to type a number that moves no
 * total. Making the line chargeable brings the control — and the price it was
 * holding all along — straight back.
 */
export function internalPriceDisplay(
  line: LineEconomics,
  amount: number,
  format: (value: number) => string,
): string {
  return chargesRevenue(line) ? format(amount) : NO_PRICE_DISPLAY;
}
